import posthog from "posthog-js";

/**
 * PostHog product analytics + session replay for the web app. Mirrors the existing imperative telemetry
 * pattern (Sentry in index.tsx): a singleton initialised once at bootstrap, with identify/reset hung off
 * the same points as Sentry.setUser.
 *
 * Env (resolved by the Vite REACT_APP_* define shim in vite.config.js):
 *   REACT_APP_POSTHOG_KEY   – project API key (integration is a no-op without it)
 *   REACT_APP_POSTHOG_HOST  – e.g. https://us.i.posthog.com | https://eu.i.posthog.com | self-host
 */

let initialised = false;

const posthogKey = () => process.env.REACT_APP_POSTHOG_KEY;

export function initPostHog(): void {
  const key = posthogKey();
  // Skip when unconfigured or under Cypress, and never double-init.
  // `Cypress` is injected by the Cypress runner at runtime and has no ambient declaration in
  // this project; same erased form as Router.tsx l.288.
  if (!key || initialised || (globalThis as typeof globalThis & { Cypress?: unknown }).Cypress) return;
  // CP-283 recipient pages: never. Recording runs with inputs unmasked and autocapture sends the
  // token-bearing URL with every click — the same reason Sentry Replay is off there (index.tsx).
  // Share views always start as a full load on /share/..., so deciding at init time is complete.
  if (globalThis.location.pathname.startsWith("/share/")) return;

  posthog.init(key, {
    api_host: process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com",
    // Only create person profiles once we've identified a real user (keeps
    // anonymous/logged-out traffic cheap and avoids PII on the anon profile).
    person_profiles: "identified_only",
    // SPA pageviews (react-router v6) are captured automatically on history changes.
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    disable_session_recording: false,
    session_recording: {
      // Capture everything (text + inputs) EXCEPT password fields, which stay
      // masked via maskInputOptions.password.
      maskAllInputs: false,
      maskInputOptions: { password: true },
    },
    loaded: (ph) => {
      if (process.env.REACT_APP_ENV !== "prod") ph.debug(false);
    },
  });
  initialised = true;
}

export interface PostHogUser {
  id?: string | undefined;
  email?: string | undefined;
  portal?: { id?: string | undefined } | null | undefined;
  /** Matches userDetails.portalRole (accounts.schema userDetailsPortalRoleSchema) */
  portalRole?:
    | {
        id?: string | undefined;
        frontendName?: string | undefined;
        roleName?: string | undefined;
        isDefaultRole?: boolean | undefined;
      }
    | undefined;
  /** Matches userDetails.seat: plan name → seat type number, e.g. { CDE: 1, PM: 0 } */
  seat?: Record<string, number> | undefined;
}

export function identifyPostHog(user: PostHogUser | undefined): void {
  if (!posthogKey() || !user?.id) return;
  posthog.identify(user.id, {
    email: user.email,
    portalId: user.portal?.id,
    role: user.portalRole,
    seat: user.seat,
  });
}

export function resetPostHog(): void {
  if (!posthogKey()) return;
  posthog.reset();
}

export { posthog };
